import type { PaginatedResponse, SuccessResponse } from "@shared/types";
import apiClient from "@/app/api/client";
import { replaceParams, territoryEndpoints } from "@/app/api/endpoints";

/** GeoJSON Polygon: coordinates are [lng, lat] pairs */
export interface GeoJSONPolygon {
	type: "Polygon";
	coordinates: [number, number][][];
}

/** GeoJSON MultiPolygon: array of polygons, each an array of rings */
export interface GeoJSONMultiPolygon {
	type: "MultiPolygon";
	coordinates: [number, number][][][];
}

export type TerritoryCoordinates = GeoJSONPolygon | GeoJSONMultiPolygon;

export interface TerritoryAssignedStaff {
	id: string;
	fullname: string;
	isPrimary: boolean;
}

export interface TerritoryAssignedBrigade {
	id: string;
	name: string;
}

export interface Territory {
	id: string;
	name: string;
	parentId?: string | null;
	organizationId: string;
	organizationName: string;
	kmzFileUrl: string;
	area: number;
	perimeter: number;
	assignedStaffCount: number;
	coordinates?: TerritoryCoordinates;
	assignedStaff?: TerritoryAssignedStaff[];
	taskCount: number;
	plantingCount: number;
	assignedBrigades: TerritoryAssignedBrigade[];
	createdAt: string;
	updatedAt: string;
}

export interface ListTerritoriesParams {
	page?: number;
	limit?: number;
	organizationId?: string;
	staffId?: string;
	parentId?: string;
	/** Skip the parentId drill-down filter — return territories at every depth. */
	flat?: boolean;
}

export interface ImportTerritoryResponse {
	territoryId: string;
	name: string;
	organizationId: string;
	kmzFileId: string;
	area: number;
	perimeter: number;
	coordinates: unknown;
	createdAt: string;
}

export interface ImportTerritoryParams {
	file: File;
	name: string;
	organizationId: string;
	parentId?: string;
}

export interface KmzPolygonPlacemark {
	index: number;
	name: string | null;
	geometryType: "polygon";
	geometry: GeoJSONPolygon;
	area: number;
	perimeter: number;
}

export interface KmzMultiPolygonPlacemark {
	index: number;
	name: string | null;
	geometryType: "multipolygon";
	geometry: GeoJSONMultiPolygon;
	area: number;
	perimeter: number;
}

export interface KmzPointPlacemark {
	index: number;
	name: string | null;
	geometryType: "point";
	lat: number;
	lng: number;
}

export type KmzPlacemark = KmzPolygonPlacemark | KmzMultiPolygonPlacemark | KmzPointPlacemark;

export interface ImportPreviewData {
	placemarks: KmzPlacemark[];
	skipped: number;
}

export interface ImportSelection {
	index: number;
	name: string;
	parentId?: string;
	/** Circular buffer radius in meters — required for point placemarks. */
	bufferRadiusM?: number;
}

export interface ImportConfirmData {
	territories: Array<{ territoryId: string; name: string; area: number; perimeter: number }>;
}

export interface CreateTerritoryInput {
	name: string;
	organizationId: string;
	parentId?: string | null;
	coordinates?: TerritoryCoordinates | null;
	area?: number | null;
	perimeter?: number | null;
}

export interface UpdateTerritoryInput {
	name?: string;
	parentId?: string | null;
	coordinates?: TerritoryCoordinates | null;
	area?: number | null;
	perimeter?: number | null;
}

export const territoryService = {
	async list(params?: ListTerritoriesParams): Promise<PaginatedResponse<Territory>> {
		const response = await apiClient.get<PaginatedResponse<Territory>>(territoryEndpoints.list, {
			params,
		});
		return response.data;
	},

	async getById(territoryId: string): Promise<Territory> {
		const url = replaceParams(territoryEndpoints.getById, { territoryId });
		const response = await apiClient.get<SuccessResponse<Territory>>(url);
		return response.data.data;
	},

	async create(data: CreateTerritoryInput): Promise<Territory> {
		const response = await apiClient.post<SuccessResponse<Territory>>(
			territoryEndpoints.create,
			data
		);
		return response.data.data;
	},

	async update(territoryId: string, data: UpdateTerritoryInput): Promise<Territory> {
		const url = replaceParams(territoryEndpoints.update, { territoryId });
		const response = await apiClient.put<SuccessResponse<Territory>>(url, data);
		return response.data.data;
	},

	async delete(territoryId: string): Promise<void> {
		const url = replaceParams(territoryEndpoints.delete, { territoryId });
		await apiClient.delete(url);
	},

	async import(params: ImportTerritoryParams): Promise<ImportTerritoryResponse> {
		const formData = new FormData();
		formData.append("file", params.file);
		formData.append("name", params.name);
		formData.append("organizationId", params.organizationId);
		if (params.parentId) {
			formData.append("parentId", params.parentId);
		}

		const response = await apiClient.post<SuccessResponse<ImportTerritoryResponse>>(
			territoryEndpoints.import,
			formData,
			{
				headers: {
					"Content-Type": "multipart/form-data",
				},
			}
		);
		return response.data.data;
	},

	async assignStaff(
		territoryId: string,
		body: Array<{ staffId: string; isPrimary?: boolean }>
	): Promise<void> {
		const url = replaceParams(territoryEndpoints.assignStaff, { territoryId });
		await apiClient.post(url, body);
	},

	async previewImport(params: { file: File; organizationId: string }): Promise<ImportPreviewData> {
		const formData = new FormData();
		formData.append("file", params.file);
		formData.append("organizationId", params.organizationId);

		const response = await apiClient.post<SuccessResponse<ImportPreviewData>>(
			territoryEndpoints.importPreview,
			formData,
			{ headers: { "Content-Type": "multipart/form-data" } }
		);
		return response.data.data;
	},

	async confirmImport(params: {
		file: File;
		organizationId: string;
		selections: ImportSelection[];
	}): Promise<ImportConfirmData> {
		const formData = new FormData();
		formData.append("file", params.file);
		formData.append("organizationId", params.organizationId);
		formData.append("selections", JSON.stringify(params.selections));

		const response = await apiClient.post<SuccessResponse<ImportConfirmData>>(
			territoryEndpoints.importConfirm,
			formData,
			{ headers: { "Content-Type": "multipart/form-data" } }
		);
		return response.data.data;
	},
};
